import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, forkJoin } from 'rxjs';
import {
  DeckMindMapListItem,
  DeckModel,
  DeckService,
} from '@core/services/deck.service';
import { MindMapModel, MindMapService } from '@core/services/mindmap.service';
import { UiTranslationService } from '@core/services/ui-translation.service';

type DeckAssociationRow = DeckMindMapListItem & { mindMapTitle: string };

@Component({
  selector: 'app-deck-association-modal',
  templateUrl: './deck-association-modal.html',
  styleUrl: './deck-association-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeckAssociationModal implements OnInit {
  private readonly deckService = inject(DeckService);
  private readonly mindMapService = inject(MindMapService);
  readonly i18n = inject(UiTranslationService).namespace('mindmap');
  readonly activeModal = inject(NgbActiveModal);

  @Input() workspaceId?: number | null;
  @Input() mindMapId?: number | null;
  @Input() nodeKey?: string | null;

  readonly decks = signal<DeckAssociationRow[]>([]);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly savingDeckId = signal<number | null>(null);
  errorMessage = '';

  ngOnInit(): void {
    this.load();
  }

  associate(deckId: number): void {
    const mindMapId = Number(this.mindMapId);
    const nodeKey = this.nodeKey?.trim() ?? '';
    if (
      this.isSaving() ||
      !Number.isInteger(deckId) ||
      deckId <= 0 ||
      !Number.isFinite(mindMapId) ||
      mindMapId <= 0 ||
      !nodeKey
    ) {
      return;
    }

    this.isSaving.set(true);
    this.savingDeckId.set(deckId);
    this.errorMessage = '';
    this.deckService
      .move(deckId, { mindMapId, nodeKey })
      .pipe(
        finalize(() => {
          this.isSaving.set(false);
          this.savingDeckId.set(null);
        }),
      )
      .subscribe({
        next: (deck) => this.activeModal.close(deck),
        error: () => {
          this.errorMessage = this.i18n.tr('deckAssociation.errorSave');
        },
      });
  }

  private load(): void {
    const workspaceId = Number(this.workspaceId);
    const mindMapId = Number(this.mindMapId);
    const nodeKey = this.nodeKey?.trim() ?? '';
    if (
      !Number.isFinite(workspaceId) ||
      workspaceId <= 0 ||
      !Number.isFinite(mindMapId) ||
      mindMapId <= 0 ||
      !nodeKey
    ) {
      this.errorMessage = this.i18n.tr('deckAssociation.invalidNode');
      return;
    }

    this.isLoading.set(true);
    forkJoin({
      decks: this.deckService.list('all'),
      mindMaps: this.mindMapService.list(),
    })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: ({ decks, mindMaps }) => {
          const titlesByMindMapId = new Map(
            mindMaps
              .filter((mindMap) => mindMap.workspaceId === workspaceId)
              .map(
                (mindMap: MindMapModel) => [mindMap.id, mindMap.title] as const,
              ),
          );
          const rows = decks
            .map((deck) => ({
              ...deck,
              mindMapTitle: this.resolveMindMapTitle(deck, titlesByMindMapId),
            }))
            .sort((left, right) => left.title.localeCompare(right.title));
          this.decks.set(rows);
        },
        error: () => {
          this.errorMessage = this.i18n.tr('deckAssociation.errorLoad');
        },
      });
  }

  private resolveMindMapTitle(
    deck: DeckMindMapListItem,
    titlesByMindMapId: ReadonlyMap<number, string>,
  ): string {
    if (!deck.mindMapId) {
      return this.i18n.tr('deckAssociation.noMindMap');
    }

    return (
      titlesByMindMapId.get(deck.mindMapId) ??
      this.i18n.tr('deckAssociation.unavailableMindMap')
    );
  }
}
